import React from 'react'
import { Button } from '@/components/ui/button'
import { ViewTabs } from '@/components/ui/tabs'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { GridColumn as Column, Grid, GridCellProps, GridToolbar } from '@progress/kendo-react-grid'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { TransactionNoteDto } from 'api/Transaction/Types/TransactionNoteDto'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import classNames from 'classnames'
import { Loading } from 'components/Loading'
import { NotesCommandCell } from 'components/Note/NotesCommandCell'
import { useNoteState } from 'components/Note/noteState'
import { NoteValue } from 'components/Note/types'
import { formatISO } from 'date-fns'
import { useApiClient } from 'utils/api/ApiClient'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { formatLocalDateUTC } from 'utils/date'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

enum NotesTab {
  CLIENT_NOTES = 'client-notes',
  TRANSACTION_NOTES = 'transaction-notes',
}

interface TransactionDetailsNotesProps {
  transaction: TransactionDto
}

const TransactionDetailsNotesPage: React.FC<TransactionDetailsNotesProps> = ({ transaction }) => {
  return (
    <ViewTabs
      defaultKey={NotesTab.CLIENT_NOTES}
      tabs={[
        {
          key: NotesTab.CLIENT_NOTES,
          translateKey: <span>Client Notes</span>,
          visible: true,
          component: <ClientNotesTable transaction={transaction} />,
        },
        {
          key: NotesTab.TRANSACTION_NOTES,
          translateKey: <span>Transaction Notes</span>,
          visible: true,
          component: <TransactionNotesTable transaction={transaction} />,
        },
      ]}
    />
  )
}

interface NoteStateProps {
  masterTransactionId: string | null
  transaction: TransactionDto
}

const useTransactionNoteState = (props: NoteStateProps) => {
  const { transaction, masterTransactionId } = props

  const walletsApiClient = useApiClient(WalletsApiClient)
  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    return walletsApiClient.getTransactionNotesByTransactionId(transaction.id)
  }, [])
  const { data: transactionNotes = [], isLoading } = useFetchOne(callback)
  const client = getClientFromTransaction(transaction)

  const handleAdd = async (note: NoteValue<TransactionNoteDto>) => {
    if (client?.id) {
      await walletsApiClient.addTransactionNote({
        clientId: client?.id,
        masterTransactionId,
        description: note.description,
        expiryDate: note.expiryDate
          ? formatISO(new Date(note.expiryDate.toString()), { representation: 'date' })
          : null,
      })
    }
  }

  const handleUpdate = async (note: NoteValue<TransactionNoteDto>) => {
    await walletsApiClient.updateTransactionNote({
      id: note.id as string,
      description: note.description,
      expiryDate: note.expiryDate
        ? formatISO(new Date(note.expiryDate.toString()), { representation: 'date' })
        : null,
    })
  }

  const handleRemove = async (note: NoteValue<TransactionNoteDto>) => {
    if (note?.id) {
      await walletsApiClient.removeTransactionNoteById(note.id)
    }
  }

  const handleAddNew = () => {
    addNew({
      id: null,
      transaction: null,
      client: null,
      Discontinued: false,
      description: null,
      expiryDate: null,
      updatedBy: null,
    })
  }

  const handleEdit = (note: NoteValue<TransactionNoteDto>) => {
    setEditNote({ ...note, expiryDate: note.expiryDate ? new Date(note.expiryDate) : null })
  }

  const { notes, setNotes, setEditNote, addNew, ...noteState } = useNoteState({
    onAdd: handleAdd,
    onUpdate: handleUpdate,
    onRemove: handleRemove,
    onEdit: handleEdit,
    notes: transactionNotes,
    forceRefresh,
  })

  const ActionCell = NotesCommandCell(noteState)

  return { ...noteState, notes, isLoading, addNew: handleAddNew, ActionCell }
}

interface ClientNotesTableProps {
  transaction: TransactionDto
}

const ClientNotesTable = (props: ClientNotesTableProps) => {
  const { transaction } = props
  const { isLoading, notes, addNew, itemChange, ActionCell } = useTransactionNoteState({
    transaction,
    masterTransactionId: null,
  })

  return (
    <Loading isLoading={isLoading}>
      <Grid
        resizable
        data={notes.filter((x: NoteValue<TransactionNoteDto>) => !x.transaction)}
        onItemChange={itemChange}
        editField='inEdit'
      >
        <GridToolbar>
          <Button title='Add new' size='xs' onClick={addNew}>
            Add new
          </Button>
        </GridToolbar>
        <Column field='client.name' title='Client' editable={false} width={180} />
        <Column
          field='description'
          title='Description'
          className={classNames('table-columns__content', 'table-columns__break-word')}
        />
        <Column
          field='expiryDate'
          title='Expiry Date'
          editor='date'
          cell={(gridCellProps: GridCellProps) => {
            if (!gridCellProps.dataItem.inEdit) {
              return (
                <td className='transaction-columns__date'>
                  {formatLocalDateUTC(gridCellProps.dataItem.expiryDate, true)}
                </td>
              )
            }
            return (
              <td className='transaction-columns__date'>
                <DatePicker
                  value={
                    gridCellProps?.dataItem?.expiryDate
                      ? new Date(gridCellProps.dataItem.expiryDate.toString())
                      : null
                  }
                  onChange={(event) => {
                    if (gridCellProps?.onChange && event?.target?.value) {
                      gridCellProps.onChange({
                        ...event,
                        ...gridCellProps,
                        dataItem: gridCellProps.dataItem,
                        field: 'expiryDate',
                        value: event.target.value,
                      })
                    }
                  }}
                />
              </td>
            )
          }}
          minResizableWidth={120}
          width={120}
        />
        <Column
          field='createdDate'
          cell={(gridCellProps: GridCellProps) => (
            <td className='transaction-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.createdDate, true)}
            </td>
          )}
          title='Created Date'
          minResizableWidth={120}
          width={120}
        />
        <Column field='updatedBy.name' title='User' editable={false} width={180} />
        <Column cell={ActionCell} width={140} />
      </Grid>
    </Loading>
  )
}

interface TransactionNotesTableProps {
  transaction: TransactionDto
}

const TransactionNotesTable = (props: TransactionNotesTableProps) => {
  const { transaction } = props
  const { isLoading, notes, addNew, itemChange, ActionCell } = useTransactionNoteState({
    transaction,
    masterTransactionId: transaction.id,
  })

  return (
    <Loading isLoading={isLoading}>
      <Grid
        resizable
        data={notes.filter((x: NoteValue<TransactionNoteDto>) => !x.id || !!x.transaction)}
        onItemChange={itemChange}
        editField='inEdit'
      >
        <GridToolbar>
          <Button title='Add new' size='xs' onClick={addNew}>
            Add new
          </Button>
        </GridToolbar>
        <Column
          field='description'
          title='Description'
          className={classNames('table-columns__content', 'table-columns__break-word')}
        />
        <Column
          field='createdDate'
          cell={(gridCellProps: GridCellProps) => (
            <td className='transaction-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.createdDate, true)}
            </td>
          )}
          title='Created Date'
          minResizableWidth={120}
          width={120}
        />
        <Column field='updatedBy.name' title='User' editable={false} />
        <Column cell={ActionCell} width={140} />
      </Grid>
    </Loading>
  )
}

const getClientFromTransaction = (data: TransactionDto) => {
  if (data.type.id === TransactionType.NegativeEquityRefund) {
    return data.tradingAccountTransactions?.[0].client
  }
  return (
    getCreditCreditOrFirstOneWallet(data.walletTransactions)?.wallet.client ||
    data?.tradingAccountTransactions?.find((x) => x.client)?.client
  )
}

export default TransactionDetailsNotesPage
